import angular from 'angular';
import receiptPaymentsDialogTemplate from './receiptPaymentsDialog.template.html';
import CSMCommonWeb from 'csm-common-web';
import booksService from '../../../booksServices';

/*******************************************************************************
 * @ngdoc  module
 * @name   csm.books.com.returnItems.receiptPaymentsDialog
 * @description
 * Payment-elsewhere discovery dialog. Any receipt with returnable
 * receivables - not just a receipt whose items were charged on-account with
 * no payment at all - can have part or all of an item's balance paid down
 * later by one or more separate Payment Receipts, which have no items of
 * their own. Neither receipt alone shows both halves of what a return needs -
 * opening the original receipt shows the items but not the payment(s) made
 * against them elsewhere; opening the other receipt shows the payment but no
 * items.
 *
 * When a receipt still has active payments against its items made on a
 * different receipt, returnItems.js's getReceipt shows a "Receipt Payments"
 * button under the checks/cards grid; this dialog opens from that button
 * (never automatically) and shows exactly what's been paid, how, how much,
 * who it's for, and which receipt it lives on.
 *******************************************************************************/
export default angular
  .module('csm.books.com.returnItems.receiptPaymentsDialog', [
    CSMCommonWeb,
    'ui.bootstrap',
    'ui.grid',
    booksService
  ])
  .service('ReceiptPaymentsDialog', DialogControllerService)
  .component('receiptPaymentsDialog', receiptPaymentsDialogComponent()).name;

/*******************************************************************************
 * DialogControllerService */
/**
 * Queries for any active payments against the receipt's own charge items and,
 * only if any are found, opens the dialog.
 *******************************************************************************/
/* @ngInject */ function DialogControllerService(
  $uibModal,
  ReturnsService,
  BusyScreen
) {
  /*---------------------------------------------------------------------*/
  /* Resolves with this receipt's currently active payments made         */
  /* elsewhere (always an array, empty if none) without opening the      */
  /* dialog. Used on receipt load to decide whether to show the "Receipt */
  /* Payments" button at all, and kept by the caller as the baseline the */
  /* loaded item balances were computed against (see paymentsChanged).   */
  /*---------------------------------------------------------------------*/
  this.getPayments = function (receiptID) {
    return ReturnsService.getReceiptPayments(receiptID).then(
      function (payments) {
        return payments || [];
      }
    );
  };

  /*---------------------------------------------------------------------*/
  /* Whether two getPayments() results differ - a payment refunded or    */
  /* voided elsewhere drops out of the list, a partial refund changes    */
  /* its amount. Either means the item balances the Returns screen       */
  /* loaded (balanceDue/creditAmount, which drive Balance Reduction) are */
  /* stale and the receipt has to be reloaded.                           */
  /*---------------------------------------------------------------------*/
  this.paymentsChanged = function (before, after) {
    const key = (payments) =>
      (payments || [])
        .map((p) => [p.itemID, p.receiptID, p.amount].join(':'))
        .sort()
        .join('|');

    return key(before) !== key(after);
  };

  /*---------------------------------------------------------------------*/
  /* Resolves with this receipt's active payments made elsewhere, looked */
  /* up fresh once the dialog closes - in ANY way: the Close button, the */
  /* dialog's own refresh() closing itself once nothing is left, or a    */
  /* dismiss (Esc). The user may have refunded/voided one of these on    */
  /* another tab while it was open (that's what goToReceipt is for), so  */
  /* the caller compares this against what the screen was loaded with.  */
  /*                                                                     */
  /* BusyScreen only wraps the two lookups below, never the modal itself */
  /* - BusyScreen's overlay is a position:fixed, full-viewport div at    */
  /* z-index 1000000 (see busyScreen.js), which sits above a uib-modal's */
  /* own z-index and swallows every click. Leaving it up for the modal's */
  /* entire open-to-close lifetime (the original shape of this function) */
  /* meant the user could never actually click Close/Refresh - the       */
  /* overlay blocked the dialog it was supposedly just indicating a      */
  /* brief wait for, so the busy screen would never clear at all.        */
  /*---------------------------------------------------------------------*/
  this.show = function (receiptID) {
    const self = this;

    /*-------------------------------------------------*/
    /* Fresh lookup, wrapped in the busy screen - used */
    /* both before opening and after closing.          */
    /*-------------------------------------------------*/
    const lookup = function () {
      BusyScreen.setBusy(true);

      return self.getPayments(receiptID).finally(function () {
        BusyScreen.setBusy(false);
      });
    };

    return lookup().then(function (payments) {
      /*-----------------------------------------------------*/
      /* Everything was refunded elsewhere since the button  */
      /* was shown - nothing left to open the dialog for.    */
      /*-----------------------------------------------------*/
      if (payments.length === 0) return payments;

      /*------------------------------------------------------*/
      /* Re-check on close AND dismiss - the dialog's own     */
      /* refresh() may have already closed itself if a refund */
      /* elsewhere left nothing active, and a dismiss (Esc)   */
      /* rejects result rather than resolving it, so either   */
      /* way ask fresh rather than assuming payments are      */
      /* still what they were when this modal was opened.     */
      /*------------------------------------------------------*/
      return $uibModal
        .open({
          backdrop: 'static',
          component: 'receiptPaymentsDialog',
          resolve: { payments: () => payments, receiptID: () => receiptID },
          size: 'lg'
        })
        .result.then(lookup)
        .catch(lookup);
    });
  };
}

/*******************************************************************************
 * receiptPaymentsDialogComponent */
/**
 * Component definition for the receipt payments dialog.
 *******************************************************************************/
/* @ngInject */ function receiptPaymentsDialogComponent() {
  return {
    bindings: { resolve: '<', modalInstance: '<' },
    controller: Controller,
    template: receiptPaymentsDialogTemplate
  };
}

/*******************************************************************************
 * Controller */
/**
 * Controller for the receipt payments dialog.
 *******************************************************************************/
/* @ngInject */ function Controller($injector) {
  const $ctrl = this;
  const $svcs = { $ctrl };

  $ctrl.$onInit = function () {
    $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
  };
}

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the dialog controller properties and functions.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs, Tools) {
  Object.assign($ctrl, {
    payments: $ctrl.resolve.payments,
    receiptID: $ctrl.resolve.receiptID,
    goToReceipt: $injector.invoke(goToReceipt, null, $svcs),
    buildGrid: $injector.invoke(buildGrid, null, $svcs),
    refresh: $injector.invoke(refresh, null, $svcs)
  });

  Object.defineProperty($ctrl, 'totalPaid', {
    get: function () {
      return Tools.roundMoney(
        $ctrl.payments.reduce(function (total, payment) {
          return total + payment.amount;
        }, 0)
      );
    }
  });

  $ctrl.buildGrid();
}

/*******************************************************************************
 * buildGrid */
/**
 * Builds the read-only grid of items/payments/receipts, with a button column
 * that navigates to the Returns screen for the receipt that payment lives on.
 *******************************************************************************/
/* @ngInject */ function buildGrid($ctrl, uiGridConstants) {
  const goToReceiptTemplate = `
    <div class="ui-grid-cell-contents text-center">
      <button class="btn btn-xs btn-default"
              uib-tooltip="Go to Return Receipt #{{row.entity.receiptNumber}}"
              tooltip-append-to-body="true"
              ng-click="grid.appScope.$ctrl.goToReceipt(row.entity)">
        <span class="glyphicon glyphicon-share-alt"></span>
      </button>
    </div>`;

  return function () {
    $ctrl.paymentsGrid = {
      enableColumnMenus: false,
      enableSorting: false,
      enableFiltering: false,
      enableRowSelection: false,
      enableFullRowSelection: false,
      enableRowHeaderSelection: false,
      multiSelect: false,
      enableSelectAll: false,
      enableColumnResizing: false,
      enableGridMenu: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      minRowsToShow: Math.min($ctrl.payments.length, 10) || 1,
      data: $ctrl.payments,
      columnDefs: [
        { field: 'itemName', displayName: 'Item', cellTooltip: true },
        { field: 'itemFor', displayName: 'For', cellTooltip: true },
        {
          field: 'amount',
          displayName: 'Amount Paid',
          cellFilter: 'currency',
          width: 130,
          cellClass: 'text-right'
        },
        {
          field: 'paymentType',
          displayName: 'Type',
          width: 80
        },
        {
          field: 'paymentNumber',
          displayName: 'Number',
          width: 160,
          cellTooltip: true
        },
        {
          field: 'receiptNumber',
          displayName: 'Paid On Receipt #',
          width: 150
        },
        {
          field: 'receiptID',
          displayName: '',
          width: 60,
          cellTemplate: goToReceiptTemplate
        }
      ]
    };
  };
}

/*******************************************************************************
 * goToReceipt */
/**
 * Opens the Returns screen for the receipt the selected payment actually lives
 * on in a new tab, leaving this dialog (and the receipt the user started on)
 * open and untouched.
 *
 * The app is only reachable through the books menu's own mID query param (see
 * MenuService.getAppMenuID()/displayApp()) - without it the app shell doesn't
 * know which menu item it was opened under. A plain origin+pathname URL drops
 * that, so the new tab has to carry the CURRENT tab's own mID forward onto the
 * new one (both tabs are the same ReturnReceipts app, just pointed at different
 * receipts, so the same mID applies to both).
 *
 * @param  payment  Selected payment row.
 *******************************************************************************/
/* @ngInject */ function goToReceipt($window, MenuService) {
  return function (payment) {
    $window.open(
      $window.location.origin +
        $window.location.pathname +
        '?mID=' +
        MenuService.getAppMenuID() +
        '#/receiptItems/' +
        payment.receiptID,
      '_blank'
    );
  };
}

/*******************************************************************************
 * refresh */
/**
 * Re-queries the server for this receipt's still-active payments made elsewhere,
 * in case the user just went and refunded one on another tab (see goToReceipt,
 * which opens that other receipt's Returns screen without closing this dialog,
 * specifically so the user can come back here and refresh once done).
 *
 * If any are still active, just rebuilds the grid in place. If none are left,
 * there's nothing further to warn about - tells the user this receipt is now
 * clear to return and closes the dialog. ItemReturn.cs's own validate() (the
 * actual server-side enforcement) independently re-confirms the same thing fresh
 * from the database at save time regardless of what this dialog showed, so
 * there's no client-side state left over from this dialog that could still block
 * the return once it's closed.
 *******************************************************************************/
/* @ngInject */ function refresh(
  $ctrl,
  ReturnsService,
  BusyScreen,
  Alert,
  Tools
) {
  return function () {
    BusyScreen.setBusy(true);

    return ReturnsService.getReceiptPayments($ctrl.receiptID)
      .then(function (payments) {
        BusyScreen.setBusy(false);

        if (!payments || payments.length === 0) {
          return Alert.show(
            'There are no active payments left against this receipt on other receipts.'
          ).then(function () {
            $ctrl.modalInstance.close();
          });
        }

        $ctrl.payments = payments;
        $ctrl.buildGrid();
      })
      .catch((error) => {
        Alert.error(Tools.serviceErrorhandler(error));
        BusyScreen.setBusy(false);
      });
  };
}
